'use client';

import { useEffect } from 'react';
import { setReferralCookie } from '@/lib/referral-cookie';

/**
 * ReferralRefCapture — invisible client component that reads `?ref=` from
 * the URL on mount and sets the `profileia_ref` cookie.
 *
 * SPEC-094 §1.2 (Cookie de Referido Extendida): on the public Market
 * routes (`/market?ref=USERNAME`, `/market/producto/...?ref=USERNAME`),
 * we capture the `ref` query parameter and set the referral cookie so
 * the backend can attribute the eventual registration to this referrer
 * (SPEC-NEGOCIO §8.3 — 30-day cookie).
 *
 * Why a client component?
 *   The product detail page (`/market/producto/[storeSlug]/[productSlug]`)
 *   is a Server Component (ISR + `generateMetadata`). Server Components
 *   can't read `document.cookie` or `window.location.search`. This
 *   tiny client component is rendered as a child of the Server
 *   Component — it renders nothing visible + only runs an effect on
 *   mount (no UI, no layout shift, no hydration concern).
 *
 * Why not use `useSearchParams`?
 *   `useSearchParams` requires a Suspense boundary on statically-rendered
 *   pages, which would force the product detail page to opt out of ISR
 *   (the page is `export const revalidate = 60`). `window.location.search`
 *   in a `useEffect` avoids the Suspense requirement entirely and runs
 *   only on the client (where the cookie lives).
 *
 * §6 Anti-Invención:
 *   - Cookie name + attributes are owned by `@/lib/referral-cookie`
 *     (`setReferralCookie`) — single source of truth, mirrors the
 *     existing `/r/[code]` impl.
 *   - NO `as any` (P-041), NO `console.*` (P-042), NO refs to docs in
 *     UI strings (P-083) — the component renders no UI.
 *
 * Spec ref: SPEC-094 §1.2, SPEC-NEGOCIO §8.3.
 */

export interface ReferralRefCaptureProps {
  /**
   * Optional explicit referrer code. When provided, the component sets
   * the cookie with THIS value (ignoring any `?ref=` in the URL). Used
   * by `/p/[username]` where the username being viewed IS the referrer
   * (no `?ref=` param needed — visiting the profile itself implies
   * attribution).
   *
   * When omitted, the component reads `?ref=` from `window.location.search`.
   */
  explicitCode?: string;
}

/**
 * Render nothing visible. This component is a side-effect-only client
 * component (reads URL → sets cookie on mount). Returns `null` so it
 * doesn't affect layout / DOM.
 */
export function ReferralRefCapture({ explicitCode }: ReferralRefCaptureProps) {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (explicitCode) {
      setReferralCookie(explicitCode);
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) setReferralCookie(ref);
  }, [explicitCode]);

  return null;
}
